import * as React from 'react'

import OptionsFooter from '~/client/src/shared/components/OptionsList/pieces/OptionsFooter'
import OptionsPanel from '~/client/src/shared/components/OptionsList/pieces/OptionsPanel'
import OptionsSeparator from '~/client/src/shared/components/OptionsList/pieces/OptionsSeparator'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { DrillDownFilterProvider } from './context'
import type {
  DrillDownFilterDensity,
  IDrillDownFilterContentProps,
  IDrillDownFilterContextValue,
} from './DrillDownFilter.types'
import DrillDownFilterBody from './DrillDownFilterBody'
import DrillDownFilterHeader from './DrillDownFilterHeader'
import useDrillDownFilter from './useDrillDownFilter'

import './DrillDownFilter.scss'

type ControlSize = 'sm' | 'default' | 'lg'

const DENSITY_SIZES: Record<
  DrillDownFilterDensity,
  { reset: ControlSize; footer: ControlSize }
> = {
  default: { reset: 'sm', footer: 'default' },
  comfortable: { reset: 'default', footer: 'lg' },
}

export default function DrillDownFilterContent<T>({
  categories,
  initialSelection = [],
  initialCategoryId,
  onApply,
  onCancel,
  onReset,
  title = Localization.translator.filters,
  className,
  maxHeightPercent,
  headerContent,
  density = 'default',
  autoFocusSearch = true,
  footerLayout = 'end',
  showRootHeader = true,
}: IDrillDownFilterContentProps<T>) {
  const ctx = useDrillDownFilter({
    categories,
    initialSelection,
    initialCategoryId,
    onReset,
  })
  const sizes = DENSITY_SIZES[density]

  const { selectedCategory, currentNode } = ctx.meta

  const headerTitle =
    currentNode && selectedCategory?.kind === 'tree'
      ? selectedCategory.getItemNodeLabel(currentNode)
      : selectedCategory
        ? `${Localization.translator.filterBy} ${selectedCategory.label}`
        : title

  const handleApply = () => {
    onApply(ctx.actions.getSelectedIds())
  }

  return (
    <div
      className={`drill-down-filter-content ${className || ''}`}
      style={
        maxHeightPercent
          ? {
              height: `${maxHeightPercent}%`,
              maxHeight: `${maxHeightPercent}%`,
            }
          : undefined
      }
    >
      <DrillDownFilterProvider
        value={ctx as IDrillDownFilterContextValue<unknown>}
      >
        <OptionsPanel className="tw:flex-1">
          {(showRootHeader || !!ctx.state.selectedCategoryId) && (
            <DrillDownFilterHeader
              title={headerTitle}
              showBackButton={!!ctx.state.selectedCategoryId}
              onBack={ctx.actions.onBack}
              onCancel={onCancel}
            />
          )}
          <DrillDownFilterBody
            resetSize={sizes.reset}
            autoFocusSearch={autoFocusSearch}
            headerContent={headerContent}
          />
          <OptionsSeparator />
          <OptionsFooter
            onCancel={onCancel}
            onApply={handleApply}
            size={sizes.footer}
            layout={footerLayout}
          />
        </OptionsPanel>
      </DrillDownFilterProvider>
    </div>
  )
}
